import { useEffect, useRef, useState } from 'react';

export default function HomePage() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState(0);
  const [selectedEmoji, setSelectedEmoji] = useState('😊');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [activeCategory, setActiveCategory] = useState('All');

  const locations = [
    { name: 'Downtown Urban Hub', category: 'Urban', description: 'Modern co-living spaces in the heart of the city with rooftop terraces, coworking areas, and vibrant community events.', price: '$850/month', img: 'https://public.readdy.ai/ai/img_res/f51f35c0c9f35586e3024228e0dab439.jpg' },
    { name: 'Tech District Collective', category: 'Tech Hub', description: 'Purpose-built co-living for professionals with high-speed internet, maker spaces, and networking lounges.', price: '$920/month', img: 'https://readdy.ai/api/search-image?query=modern%20tech%20coliving%20space%20with%20coworking%20area%2C%20minimalist%20design%2C%20natural%20light%2C%20collaborative%20workspace%2C%20contemporary%20furniture&width=120&height=120&seq=213&orientation=squarish' },
    { name: 'Creative Quarter Studios', category: 'Creative', description: 'Artist-friendly co-living with studio spaces, gallery walls, and creative community workshops.', price: '$780/month', img: 'https://readdy.ai/api/search-image?query=creative%20coliving%20studio%20space%20with%20art%20supplies%2C%20bright%20natural%20lighting%2C%20industrial%20design%2C%20artistic%20community%20environment&width=120&height=120&seq=214&orientation=squarish' },
    { name: 'Suburban Community Living', category: 'Suburban', description: 'Spacious co-living homes with gardens, outdoor spaces, and family-friendly community areas.', price: '$720/month', img: 'https://readdy.ai/api/search-image?query=suburban%20coliving%20house%20with%20garden%2C%20modern%20residential%20architecture%2C%20community%20outdoor%20space%2C%20welcoming%20entrance&width=120&height=120&seq=215&orientation=squarish' },
    { name: 'Waterfront Residences', category: 'Urban', description: 'Premium co-living with water views, fitness centers, and wellness-focused community programs.', price: '$1,100/month', img: 'https://readdy.ai/api/search-image?query=waterfront%20coliving%20building%20with%20modern%20design%2C%20large%20windows%2C%20urban%20waterside%20location%2C%20contemporary%20architecture&width=120&height=120&seq=216&orientation=squarish' },
    { name: 'Green Living Campus', category: 'Eco', description: 'Sustainable co-living with solar panels, community gardens, and eco-conscious residents.', price: '$800/month', img: 'https://readdy.ai/api/search-image?query=eco%20friendly%20coliving%20building%20with%20green%20features%2C%20sustainable%20architecture%2C%20solar%20panels%2C%20natural%20materials%2C%20modern%20design&width=120&height=120&seq=217&orientation=squarish' }
  ];

  const filteredLocations = activeCategory === 'All' 
    ? locations 
    : locations.filter(loc => loc.category === activeCategory);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const sectionWidth = container.clientWidth;
      const currentSection = Math.round(scrollLeft / sectionWidth);
      setActiveSection(currentSection);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const sectionWidth = container.clientWidth;
    container.scrollTo({
      left: sectionWidth * index,
      behavior: 'smooth'
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5d0uj7dcu97hjrn44a0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setSelectedEmoji('😊');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full flex justify-between items-center px-8 py-4 z-50 bg-white bg-opacity-95 backdrop-blur-md shadow-sm">
        <div className="text-2xl font-bold text-[#FF6B35]">CoLivingHub</div>
        <div className="hidden lg:flex gap-8">
          <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection(0); }} className="text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Home</a>
          <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection(1); }} className="text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Services</a>
          <a href="#benefits" onClick={(e) => { e.preventDefault(); scrollToSection(2); }} className="text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Benefits</a>
          <a href="#community" onClick={(e) => { e.preventDefault(); scrollToSection(3); }} className="text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Community</a>
          <a href="#testimonials" onClick={(e) => { e.preventDefault(); scrollToSection(4); }} className="text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Testimonials</a>
          <a href="#locations" onClick={(e) => { e.preventDefault(); scrollToSection(5); }} className="text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Locations</a>
          <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection(6); }} className="text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Contact</a>
        </div>
        <button onClick={() => scrollToSection(6)} className="bg-[#FF6B35] text-white px-6 py-3 rounded-lg whitespace-nowrap hover:bg-opacity-90 transition-all cursor-pointer">Partner With Us</button>
      </nav>

      {/* Scroll Indicator */}
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 flex flex-row gap-3">
        {[0, 1, 2, 3, 4, 5, 6].map((index) => (
          <div
            key={index}
            onClick={() => scrollToSection(index)}
            className={`w-3 h-3 rounded-full cursor-pointer transition-all ${
              activeSection === index ? 'bg-[#FF6B35] scale-125' : 'bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>

      {/* Horizontal Scroll Container */}
      <div ref={scrollContainerRef} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', height: 'calc(100vh - 0px)' }}>
        {/* Section 1: Home */}
        <section id="home" className="min-w-full snap-start relative flex-shrink-0" style={{ height: '100vh' }}>
          <div className="absolute inset-0 bg-black">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=diverse%20multicultural%20group%20of%20young%20professionals%20from%20different%20ethnicities%20including%20Asian%20Black%20Hispanic%20White%20Middle%20Eastern%20people%20socializing%20together%20in%20modern%20coliving%20community%20space%2C%20bright%20open%20floor%20plan%20with%20contemporary%20furniture%20and%20natural%20lighting%2C%20collaborative%20atmosphere%20showing%20people%20working%20and%20connecting%20in%20shared%20living%20environment&width=1920&height=1080&seq=301&orientation=landscape)' }}></div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40"></div>
          <div className="relative h-full flex items-center">
            <div className="container mx-auto px-16 py-24 text-white w-full">
              <h1 className="text-6xl mb-6 max-w-3xl leading-tight font-bold">Transform Your Property Into a Thriving Co-Living Community</h1>
              <p className="max-w-2xl text-xl mb-8 leading-relaxed">Expert property management services for modern co-living spaces. We handle everything from tenant matching to community building, maximizing your returns while creating vibrant living experiences.</p>
              <div className="flex gap-6">
                <button onClick={() => scrollToSection(1)} className="bg-[#FF6B35] text-white px-8 py-3 rounded-lg whitespace-nowrap hover:bg-opacity-90 transition-all cursor-pointer">Our Services</button>
                <button onClick={() => scrollToSection(6)} className="border-2 border-white text-white px-8 py-3 rounded-lg whitespace-nowrap hover:bg-white hover:text-[#FF6B35] transition-all cursor-pointer">Get Started</button>
              </div>
            </div>
          </div>
          <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex items-center gap-2 text-white">
            <span>Scroll to explore</span>
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-arrow-right-line ri-lg"></i>
            </div>
          </div>
        </section>

        {/* Section 2: Services */}
        <section id="services" className="min-w-full snap-start relative flex-shrink-0" style={{ height: '100vh' }}>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=diverse%20group%20of%20people%20from%20different%20ethnicities%20including%20African%20American%20Asian%20Hispanic%20Caucasian%20cooking%20together%20in%20modern%20coliving%20shared%20kitchen%20and%20dining%20area%2C%20community%20interaction%20and%20bonding%2C%20bright%20contemporary%20interior%20design%20with%20open%20concept%20space%20and%20natural%20wood%20and%20white%20tones&width=1920&height=1080&seq=302&orientation=landscape)' }}></div>
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="relative h-full flex items-center justify-center">
            <div className="container mx-auto px-16 py-24">
              <div className="bg-white bg-opacity-95 backdrop-blur-md p-12 rounded-lg max-w-2xl">
                <h2 className="text-4xl mb-6 font-bold">Full-Service Co-Living Management</h2>
                <p className="mb-8 leading-relaxed">We provide comprehensive property management tailored specifically for co-living spaces, ensuring smooth operations and happy residents.</p>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center text-[#FF6B35] bg-[#FF6B35]/10 rounded-lg flex-shrink-0">
                      <i className="ri-team-line ri-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Tenant Matching & Screening</h3>
                      <p className="text-sm">Curated community building through careful tenant selection and compatibility matching.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center text-[#FF6B35] bg-[#FF6B35]/10 rounded-lg flex-shrink-0">
                      <i className="ri-calendar-event-line ri-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Community Events & Programs</h3>
                      <p className="text-sm">Regular social events, workshops, and activities that foster connection and engagement.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center text-[#FF6B35] bg-[#FF6B35]/10 rounded-lg flex-shrink-0">
                      <i className="ri-tools-line ri-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">24/7 Maintenance & Support</h3>
                      <p className="text-sm">Round-the-clock property maintenance and resident support for peace of mind.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center text-[#FF6B35] bg-[#FF6B35]/10 rounded-lg flex-shrink-0">
                      <i className="ri-line-chart-line ri-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Revenue Optimization</h3>
                      <p className="text-sm">Dynamic pricing strategies and occupancy management to maximize your property income.</p>
                    </div>
                  </div>
                </div>
                <button className="bg-[#FF6B35] text-white px-8 py-3 rounded-lg whitespace-nowrap hover:bg-opacity-90 transition-all cursor-pointer w-full">Learn More</button>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Benefits */}
        <section id="benefits" className="min-w-full snap-start flex items-center justify-center flex-shrink-0 bg-[#F8F9FA]" style={{ height: '100vh' }}>
          <div className="container mx-auto px-16 py-24">
            <div className="max-w-5xl mx-auto bg-white p-12 rounded-lg shadow-lg">
              <h2 className="text-4xl mb-8 text-center font-bold">Why Choose Co-Living Management</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="mb-6">
                    <div className="w-full h-48 bg-white shadow-md mb-4 rounded-lg overflow-hidden">
                      <img src="https://readdy.ai/api/search-image?query=financial%20growth%20chart%20showing%20increasing%20rental%20revenue%2C%20property%20management%20analytics%20dashboard%2C%20business%20success%20metrics%2C%20professional%20data%20visualization%20with%20upward%20trends&width=600&height=400&seq=203&orientation=landscape" alt="Higher Returns" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-[#FF6B35]">Higher Returns</h3>
                    <p className="leading-relaxed text-sm">Co-living properties generate 20-40% higher revenue compared to traditional rentals through optimized space utilization and premium pricing.</p>
                  </div>
                </div>
                <div>
                  <div className="mb-6">
                    <h3 className="text-xl font-bold mb-3 text-[#FF6B35]">Property Owner Benefits</h3>
                    <div className="border-l-4 border-[#FF6B35] pl-3 mb-4">
                      <p className="text-sm"><span className="font-bold">Lower Vacancy Rates:</span> Our community-focused approach keeps occupancy rates above 95% year-round.</p>
                    </div>
                    <div className="border-l-4 border-[#FF6B35] pl-3 mb-4">
                      <p className="text-sm"><span className="font-bold">Reduced Turnover:</span> Happy residents stay longer, reducing costly turnover and vacancy periods.</p>
                    </div>
                    <div className="border-l-4 border-[#FF6B35] pl-3 mb-4">
                      <p className="text-sm"><span className="font-bold">Professional Management:</span> We handle all operations, from maintenance to conflict resolution.</p>
                    </div>
                    <div className="border-l-4 border-[#FF6B35] pl-3">
                      <p className="text-sm"><span className="font-bold">Brand Building:</span> We create a unique identity for your property that attracts quality tenants.</p>
                    </div>
                  </div>
                  <div className="bg-white p-3 shadow-md rounded-lg overflow-hidden">
                    <img src="https://readdy.ai/api/search-image?query=diverse%20multiethnic%20property%20owner%20team%20including%20Asian%20Black%20Hispanic%20White%20professionals%20reviewing%20positive%20financial%20reports%20together%2C%20satisfied%20landlords%20with%20property%20management%20team%20in%20professional%20business%20meeting%2C%20handshake%20and%20success%20celebration%20showing%20inclusivity&width=600&height=400&seq=304&orientation=landscape" alt="Satisfied Owners" className="w-full h-auto object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Community */}
        <section id="community" className="min-w-full snap-start flex-shrink-0 bg-[#FFF5F2]" style={{ height: '100vh' }}>
          <div className="container mx-auto px-16 py-24 h-full flex flex-col justify-center">
            <h2 className="text-4xl mb-12 text-center font-bold">Building Vibrant Communities</h2>
            <div className="relative w-full h-[500px] mx-auto max-w-6xl rounded-lg overflow-hidden">
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=diverse%20multicultural%20group%20of%20coliving%20residents%20from%20various%20ethnicities%20including%20African%20American%20Asian%20Hispanic%20Middle%20Eastern%20Caucasian%20people%20enjoying%20rooftop%20community%20event%20together%2C%20people%20laughing%20and%20socializing%20with%20string%20lights%20and%20urban%20skyline%20background%20at%20sunset%2C%20inclusive%20multicultural%20community%20gathering%20with%20food%20and%20drinks%20celebrating%20diversity&width=1200&height=800&seq=305&orientation=landscape)' }}></div>
            </div>
            <div className="mt-12 text-center">
              <p className="max-w-2xl mx-auto mb-6">We don't just manage properties—we cultivate communities. Through carefully curated events, shared spaces, and thoughtful design, we create environments where residents thrive, connect, and build lasting relationships.</p>
              <button className="bg-[#FF6B35] text-white px-6 py-3 rounded-lg whitespace-nowrap hover:bg-opacity-90 transition-all cursor-pointer">See Our Communities</button>
            </div>
          </div>
        </section>

        {/* Section 5: Testimonials */}
        <section id="testimonials" className="min-w-full snap-start flex-shrink-0 bg-[#F0F0F0]" style={{ height: '100vh' }}>
          <div className="container mx-auto px-16 py-16 h-full flex flex-col justify-center">
            <h2 className="text-3xl mb-6 text-center font-bold">What Our Residents Say</h2>
            <p className="text-center max-w-2xl mx-auto mb-8 text-sm">Hear from the people who call our co-living spaces home.</p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl mx-auto">
              {[
                { img: 'https://readdy.ai/api/search-image?query=young%20Asian%20professional%20woman%20smiling%20happily%20in%20modern%20coliving%20bedroom%20with%20laptop%20on%20desk%2C%20bright%20natural%20light%20streaming%20through%20windows%2C%20contemporary%20minimalist%20decor%2C%20happy%20resident%20testimonial%20portrait&width=400&height=400&seq=306&orientation=squarish', caption: 'Best living experience ever! 🏠', user: '@sarah_tech', likes: '428' },
                { img: 'https://readdy.ai/api/search-image?query=diverse%20group%20of%20coliving%20friends%20including%20Black%20Hispanic%20Asian%20White%20residents%20cooking%20together%20in%20shared%20kitchen%2C%20multicultural%20young%20adults%20laughing%20and%20preparing%20meal%20together%2C%20community%20bonding%20moment%20showing%20inclusivity&width=400&height=400&seq=307&orientation=squarish', caption: 'Found my community here ❤️', user: '@mike_creative', likes: '612' },
                { img: 'https://readdy.ai/api/search-image?query=Hispanic%20male%20coliving%20resident%20working%20from%20home%20in%20coworking%20space%2C%20focused%20professional%20at%20desk%20with%20modern%20office%20setup%2C%20productive%20environment%2C%20natural%20lighting&width=400&height=400&seq=308&orientation=squarish', caption: 'Perfect work-life balance 💼', user: '@alex_startup', likes: '389' },
                { img: 'https://readdy.ai/api/search-image?query=diverse%20multiethnic%20coliving%20rooftop%20yoga%20session%20at%20sunrise%20with%20people%20from%20different%20backgrounds%20including%20African%20American%20Asian%20Hispanic%20doing%20yoga%20poses%20together%2C%20peaceful%20morning%20atmosphere%20showing%20urban%20wellness%20community%20and%20inclusivity&width=400&height=400&seq=309&orientation=squarish', caption: 'Love the wellness programs 🧘', user: '@wellness_jen', likes: '521' },
                { img: 'https://readdy.ai/api/search-image?query=multicultural%20coliving%20game%20night%20with%20diverse%20residents%20including%20Black%20White%20Asian%20Middle%20Eastern%20people%20playing%20board%20games%20together%2C%20people%20laughing%20around%20table%20in%20cozy%20lounge%20atmosphere%2C%20community%20entertainment%20showing%20diversity&width=400&height=400&seq=310&orientation=squarish', caption: 'Never a dull moment! 🎮', user: '@gamer_chris', likes: '445' },
                { img: 'https://readdy.ai/api/search-image?query=Black%20female%20coliving%20resident%20relaxing%20in%20modern%20common%20area%20with%20book%2C%20comfortable%20seating%20surrounded%20by%20plants%2C%20peaceful%20reading%20nook%20with%20natural%20light%20streaming%20in&width=400&height=400&seq=311&orientation=squarish', caption: 'My peaceful sanctuary 📚', user: '@bookworm_emma', likes: '367' }
              ].map((item, index) => (
                <div key={index} className="bg-white p-2.5 shadow-md transform hover:scale-105 transition-transform rounded-lg">
                  <img src={item.img} alt={item.caption} className="w-full h-auto object-cover mb-2 rounded-lg" />
                  <div className="p-1.5">
                    <p className="font-medium text-xs mb-1">{item.caption}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs">{item.user}</span>
                      <div className="flex items-center gap-1 ml-auto text-xs">
                        <div className="w-4 h-4 flex items-center justify-center text-[#FF6B35]">
                          <i className="ri-heart-line"></i>
                        </div>
                        <span>{item.likes}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 6: Locations */}
        <section id="locations" className="min-w-full snap-start relative flex-shrink-0" style={{ height: '100vh' }}>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=city%20map%20with%20coliving%20location%20pins%2C%20urban%20planning%20visualization%2C%20multiple%20property%20locations%20marked%2C%20modern%20real%20estate%20portfolio%20overview%2C%20professional%20business%20graphics&width=1920&height=1080&seq=218&orientation=landscape)' }}></div>
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="relative container mx-auto px-16 py-24 h-full flex flex-col justify-center">
            <div className="bg-white/30 backdrop-blur-xl p-10 rounded-lg max-w-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/20">
              <h2 className="text-4xl mb-4 text-white font-bold">Our Co-Living Locations</h2>
              <p className="mb-6 text-white/90">Discover our managed co-living properties across prime urban and suburban locations.</p>
              <div className="flex flex-wrap gap-3 mb-6">
                {['All', 'Urban', 'Tech Hub', 'Creative', 'Suburban', 'Eco'].map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap cursor-pointer transition-all ${
                      activeCategory === category
                        ? 'bg-[#FF6B35] text-white'
                        : 'border border-white text-white hover:bg-white hover:text-[#FF6B35]'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
              <div className="space-y-4 max-h-[320px] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#FF6B35 transparent' }}>
                {filteredLocations.map((location, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <img src={location.img} alt={location.name} className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
                    <div>
                      <h3 className="font-bold mb-1 text-white text-sm">{location.name}</h3>
                      <p className="text-xs mb-1 text-white/90">{location.description}</p>
                      <div className="flex items-center gap-2 text-xs text-[#FF6B35] font-bold">
                        <span>From {location.price}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center">
                <button className="bg-[#FF6B35] text-white px-6 py-3 rounded-lg whitespace-nowrap hover:bg-opacity-90 transition-all cursor-pointer">View All Properties</button>
              </div>
            </div>
          </div>
        </section>

        {/* Section 7: Contact */}
        <section id="contact" className="min-w-full snap-start flex-shrink-0 bg-[#F8F9FA]" style={{ height: '100vh' }}>
          <div className="container mx-auto px-16 py-16 h-full flex flex-col justify-center">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl mb-4 font-bold">Let's Work Together</h2>
              <p className="mb-12 text-lg text-gray-600">Ready to transform your property into a thriving co-living community? Reach out to us directly.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                {/* Email Card */}
                <a 
                  href="mailto:christiankittl@yahoo.com" 
                  className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-[#FF6B35]/10 rounded-full mx-auto mb-4 group-hover:bg-[#FF6B35]/20 transition-all">
                    <i className="ri-mail-line ri-2x text-[#FF6B35]"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Email Us</h3>
                  <p className="text-[#FF6B35] font-medium text-lg">christiankittl@yahoo.com</p>
                  <p className="text-gray-500 text-sm mt-2">We typically respond within 24 hours</p>
                </a>

                {/* Phone Card */}
                <a 
                  href="tel:8132307608" 
                  className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-[#FF6B35]/10 rounded-full mx-auto mb-4 group-hover:bg-[#FF6B35]/20 transition-all">
                    <i className="ri-phone-line ri-2x text-[#FF6B35]"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Call or Text</h3>
                  <p className="text-[#FF6B35] font-medium text-lg">(813) 230-7608</p>
                  <p className="text-gray-500 text-sm mt-2">Available for calls and text messages</p>
                </a>
              </div>

              <div className="bg-white/50 backdrop-blur-sm p-6 rounded-lg inline-block">
                <p className="text-gray-600">Whether you're a property owner looking for management services or interested in becoming a resident, we'd love to hear from you!</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
